import { ContactListSidebar } from "@/components/contacts/ContactListSidebar.tsx";
import { CommunicationSidebar } from "./CommunicationSidebar.tsx";
import { CommunicationThreadSidebar } from "./CommunicationThreadSidebar.tsx";
import { Sidebar, SidebarProvider } from "@/components/ui/sidebar.tsx";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { useLocalStorage } from "@/hooks/useLocalStorage.ts";
import { cn } from "@/lib/utils.ts";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useRightSidebar, useRightSidebarQueryParamState } from "./hooks.tsx";
import type { SidebarSchema } from "./sidebarSchema.ts";

/**
 * Registry mapping sidebar type → component. Adding a new sidebar type to
 * SidebarSchema without a corresponding entry here will cause a TypeScript error.
 */
// biome-ignore lint/suspicious/noExplicitAny: each component expects its own schema variant, but they're rendered generically via the discriminated union
const sidebarComponents: Record<SidebarSchema["type"], React.ComponentType<any>> = {
  contactList: ContactListSidebar,
  communication: CommunicationSidebar,
  communicationThread: CommunicationThreadSidebar,
};

/** Accessible titles for the mobile sheet, keyed by sidebar type. */
const sidebarTitles: Record<SidebarSchema["type"], string> = {
  contactList: "People",
  communication: "Communication",
  communicationThread: "Conversation",
};

const RightSidebarBody = ({
  queryParamSidebarState,
  isResizing,
  onResizeStart,
}: {
  queryParamSidebarState: SidebarSchema | null;
  /** True while the user is dragging the resize handle. Disables width transitions so the sidebar tracks the cursor without lag. */
  isResizing: boolean;
  /** Pointerdown handler for the resize handle. */
  onResizeStart: (e: React.PointerEvent<HTMLDivElement>) => void;
}) => {
  const isMobile = useIsMobile();
  const { closeSidebar } = useRightSidebar();

  // TODO: could we do this better with a motion presence animation?
  // Desktop-only: keep the sidebar content in place while it closes (so it
  // can animate out) by caching the last non-null sidebar state. The mobile
  // drill-down has no exit animation, so we skip the cache there to avoid
  // populating state that's never read.
  const [cachedSidebarState, setCachedSidebarState] = useState<typeof queryParamSidebarState>(null);
  useEffect(() => {
    if (isMobile) return;
    if (queryParamSidebarState) {
      setCachedSidebarState(queryParamSidebarState);
    }
  }, [queryParamSidebarState, isMobile]);
  const onAnimationComplete = useCallback((state: "expanded" | "collapsed") => {
    if (state === "collapsed") {
      setCachedSidebarState(null);
    }
  }, []);

  // To prevent off by 1's with the cached state, we prefer to use the query param state when it's present,
  // This means the cached state should only be used immediately after the query param state is set to null
  const sidebarState = queryParamSidebarState ?? cachedSidebarState;

  // Escape key closes the mobile drill-down view (replaces what Radix's
  // Dialog gave us before — we render a plain overlay now instead of a
  // shadcn Sheet, so we wire this ourselves).
  //
  // Defer to inner Radix dialogs first: ContactList mounts a delete-confirm
  // Dialog and a MergeContactDialog, both of which call `preventDefault()`
  // on the Escape they handle. If `defaultPrevented` is set, the inner
  // dialog already consumed the keystroke — closing the whole sidebar on
  // top would yank the user out of context unintentionally.
  useEffect(() => {
    if (!isMobile || !queryParamSidebarState) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      closeSidebar();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isMobile, queryParamSidebarState, closeSidebar]);

  // On mobile, render sidebar content as a full-screen drill-down view —
  // standard page bg, no backdrop overlay, no slide-up animation. Each
  // sidebar component supplies its own header + close affordance, so this
  // wrapper only handles routing + escape-to-close. The parent page stays
  // mounted underneath so dismissing is instant.
  if (isMobile) {
    if (!queryParamSidebarState) return null;
    const { type } = queryParamSidebarState;
    const Component = sidebarComponents[type];
    return (
      <div
        className="fixed inset-0 z-50 flex flex-col bg-background text-foreground"
        role="dialog"
        aria-modal="true"
        aria-label={sidebarTitles[type]}
      >
        <Component {...queryParamSidebarState} />
      </div>
    );
  }

  return (
    <Sidebar
      side="right"
      collapsible="offcanvas"
      onAnimationComplete={onAnimationComplete}
      // `!transition-none` is a Tailwind important override for the Sidebar's
      // baked-in width transition. While dragging the resize handle we need
      // the width to track the cursor in real time, not lag 200ms behind.
      className={isResizing ? "!transition-none" : undefined}
    >
      {/* Resize handle — 4px wide affordance on the left edge of the sidebar.
          Hit area is widened via negative margin so it's easier to grab; the
          visible highlight only shows on hover/active. Desktop only: mobile
          renders a Sheet above, and this branch isn't reached on mobile. */}
      <div
        onPointerDown={onResizeStart}
        className={cn(
          "absolute inset-y-0 left-0 w-1 -translate-x-1/2 z-20",
          "cursor-ew-resize touch-none select-none",
          "before:absolute before:inset-y-0 before:-inset-x-1 before:content-['']",
          "hover:bg-accent/60 active:bg-accent",
          isResizing && "bg-accent",
        )}
      />
      {sidebarState &&
        (() => {
          const Component = sidebarComponents[sidebarState.type];
          return <Component {...sidebarState} />;
        })()}
    </Sidebar>
  );
};

/**
 * Min/max bounds for the user-resizable right sidebar width, in pixels.
 *
 * - Min (320px) matches the tablet default — below this the calendar's
 *   week/month views clip and the thread sidebar loses breathing room.
 * - Max (720px) is a soft cap: past this, the sidebar eats too much of
 *   narrower viewports and feels like a modal rather than a panel.
 */
const SIDEBAR_MIN_WIDTH_PX = 320;
const SIDEBAR_MAX_WIDTH_PX = 720;

function clampSidebarWidth(px: number): number {
  return Math.min(Math.max(px, SIDEBAR_MIN_WIDTH_PX), SIDEBAR_MAX_WIDTH_PX);
}

/**
 * Roughly what the thread list beside the canvas takes. Not read from the DOM:
 * this only feeds the default width below, which is a starting point the user
 * can drag, so being a few pixels out costs nothing and a measurement would tie
 * this component to a layout it does not own.
 */
const THREAD_LIST_WIDTH_PX = 400;

/**
 * One third of the space the sidebar shares with the canvas — the viewport, less
 * the thread list beside it — so a conversation and the artifact it opened rest
 * on a 2:1 split rather than the artifact getting a 320px slice of a wide screen.
 *
 * A starting point only: the stored width wins once the user has dragged the
 * handle, and the bounds above are what narrow viewports get instead.
 */
function defaultSidebarWidth(): number {
  if (typeof window === "undefined") return SIDEBAR_MIN_WIDTH_PX;
  return clampSidebarWidth(Math.round((window.innerWidth - THREAD_LIST_WIDTH_PX) / 3));
}

/**
 * Where the sidebar mounts. Empty by default, which is beside the whole page —
 * every screen but home. A screen that names a host puts the sidebar inside that
 * element instead; see {@link RightSidebarHost}.
 */
const RightSidebarHostContext = createContext<{
  host: HTMLElement | null;
  setHost: (el: HTMLElement | null) => void;
}>({ host: null, setHost: () => {} });

/**
 * Marks the column the right sidebar should open inside, rather than beside the
 * whole page.
 *
 * The sidebar is two elements: a `fixed` panel, which is out of flow and pins to
 * the viewport either way, and an in-flow spacer whose animating width is what
 * pushes content aside. Rendered at the page level that spacer shrinks the whole
 * page — so opening the sidebar reflowed the thread list as well as the canvas,
 * and re-resolved every container query in the page for the length of the
 * animation. Mounting it in the canvas column instead means the column the
 * artifact belongs to is the only thing that gives ground.
 *
 * Render it as a row sibling of the content it should shrink. Screens that do
 * not use it are unaffected.
 */
export const RightSidebarHost = () => {
  const { setHost } = useContext(RightSidebarHostContext);
  // Cleared on unmount so a screen that stops rendering a host falls back to
  // mounting the sidebar at the page level rather than into a detached node.
  useEffect(() => () => setHost(null), [setHost]);
  return (
    <div
      ref={setHost}
      className={cn(
        "flex shrink-0",
        // Below xl the column is too narrow to give any of itself away, so the
        // sidebar overlays instead of pushing: hide the in-flow spacer and let
        // the `fixed` panel lie over the content. The same rule the page-level
        // mount states from the provider.
        "max-xl:[&_[data-state=expanded]>div:first-child]:hidden",
      )}
    />
  );
};

/**
 * Parent component for the right sidebar. Actual content is dynamic, and is
 * determined by query params.
 *
 * @see {@link ./README.md}
 */
export const RightSidebarProvider = ({
  children,
  className,
  overlayable,
}: {
  children: React.ReactNode;
  className?: string;
  /** Hacky workaround for layout issues from sidebar squishing on pages wide layouts. Should revisit when we revisit sidebar behavior. */
  overlayable?: boolean;
}) => {
  // Get the sidebar state based on the URL query params
  const queryParamSidebarState = useRightSidebarQueryParamState();
  const { closeSidebar } = useRightSidebar();
  const isOpen = !!queryParamSidebarState;

  // Which column the sidebar mounts in, published by `RightSidebarHost` above.
  const [host, setHost] = useState<HTMLElement | null>(null);
  const hostValue = useMemo(() => ({ host, setHost }), [host]);

  // Persisted width (pixels) — user can drag the left-edge handle to resize,
  // and the choice survives reloads. See `defaultSidebarWidth` for the start.
  // Below 768px we route to a page anyway (slice 4 registry), so this width
  // doesn't apply to mobile.
  // Read once per mount: the default only matters before the user has dragged
  // the handle, and re-measuring the viewport on every render is a layout read
  // for a value that is about to be ignored.
  const [defaultWidth] = useState(defaultSidebarWidth);
  const [storedWidth, setStoredWidth] = useLocalStorage<number>("rightSidebarWidth", defaultWidth);
  const sidebarWidthPx = clampSidebarWidth(storedWidth);

  // Resize state: `isResizing` is true while a drag is in flight; dragStart
  // captures the pointer's x and the sidebar's width at drag start so we can
  // compute deltas in the move handler without re-registering it.
  const [isResizing, setIsResizing] = useState(false);
  const dragStartRef = useRef<{ x: number; width: number } | null>(null);

  const onResizeStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragStartRef.current = { x: e.clientX, width: sidebarWidthPx };
      setIsResizing(true);
    },
    [sidebarWidthPx],
  );

  // Attach document-level pointermove/pointerup only while resizing. The
  // handler reads dragStartRef (no stale closure) and commits the new width
  // via setStoredWidth — useLocalStorage also persists to localStorage.
  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e: PointerEvent) => {
      const start = dragStartRef.current;
      if (!start) return;
      // Sidebar is on the right edge; moving the pointer LEFT should widen it.
      const next = clampSidebarWidth(start.width + (start.x - e.clientX));
      setStoredWidth(next);
    };
    const onUp = () => {
      dragStartRef.current = null;
      setIsResizing(false);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
    return () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
    };
  }, [isResizing, setStoredWidth]);

  const sidebarWidth = `${sidebarWidthPx}px`;

  const body = (
    <RightSidebarBody
      queryParamSidebarState={queryParamSidebarState}
      isResizing={isResizing}
      onResizeStart={onResizeStart}
    />
  );

  return (
    <RightSidebarHostContext.Provider value={hostValue}>
      <SidebarProvider
        defaultOpen={false}
        open={isOpen}
        onOpenChange={(newOpen) => {
          if (!newOpen) {
            closeSidebar();
          } else {
            console.error("can not open sidebar from onOpenChange - no content to show");
          }
        }}
        style={{
          "--sidebar-width": sidebarWidth,
          "--sidebar-width-mobile": sidebarWidth,
        }}
        className={cn(
          className,
          // Until xl, sidebar should overlay when this is true. (Note that on mobile it routes to a new page anyways.)
          overlayable ? "max-xl:[&>[data-state=expanded]>div:first-child]:hidden" : "",
          // While resizing, stop the browser from interpreting cursor movement
          // as text selection, and keep the ew-resize cursor pinned globally.
          isResizing && "select-none cursor-ew-resize",
        )}
      >
        {children}
        {/* Into the column that named itself the host, if any — otherwise
            here, beside the whole page, which is every screen but home. */}
        {host ? createPortal(body, host) : body}
      </SidebarProvider>
    </RightSidebarHostContext.Provider>
  );
};
